import { useEffect, useState } from "react";
import { Link, navigate } from "../app/router";
import { useSession } from "../app/session";
import { api, deriveKey } from "../sync/api";
import * as local from "../sync/localdb";
import { Button, Tape, useToast } from "../ui/kit";
import { RecoveryKeyCard } from "./Auth";
import { importGuestBoards } from "./guest";

export function Account() {
  const { user, setUser, signOut } = useSession();
  const toast = useToast();
  const [name, setName] = useState(user?.name ?? "");
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [keyPw, setKeyPw] = useState("");
  const [newKey, setNewKey] = useState<string | null>(null);
  const [delPw, setDelPw] = useState("");
  const [guestCount, setGuestCount] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Account · Sepia";
    local.listGuestBoards().then((b) => setGuestCount(b.length));
  }, []);

  if (!user) {
    return (
      <div className="page-center">
        <p>
          Please <Link to="/login?next=/account">log in</Link>.
        </p>
      </div>
    );
  }

  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    try {
      await fn();
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="account-page">
      <header className="account-top">
        <Link to="/" className="brand">
          <img src="/favicon-64.png" alt="" width="32" height="32" />
          <span className="wordmark">sepia</span>
        </Link>
        <Link to="/" className="btn btn-plain">← Back to boards</Link>
      </header>
      <div className="account-grid">
        <section className="account-card">
          <Tape color="#93ad86" className="auth-tape" />
          <h2>You</h2>
          <label className="field">
            <span>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
          </label>
          <label className="field">
            <span>E-mail</span>
            <input value={user.email} readOnly />
          </label>
          <Button kind="primary" disabled={!!busy || name.trim() === user.name} onClick={() => run("name", async () => { const r = await api.updateProfile({ name }); setUser(r.user); toast("Saved"); })}>
            Save name
          </Button>
        </section>

        <section className="account-card">
          <Tape color="#d9b25a" className="auth-tape" />
          <h2>Password</h2>
          <label className="field">
            <span>Current password</span>
            <input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} autoComplete="current-password" />
          </label>
          <label className="field">
            <span>New password</span>
            <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" minLength={8} />
          </label>
          <Button
            kind="primary"
            disabled={!!busy || !oldPw || newPw.length < 8}
            onClick={() =>
              run("pw", async () => {
                await api.changePassword(await deriveKey(oldPw, user.email), await deriveKey(newPw, user.email));
                setOldPw("");
                setNewPw("");
                toast("Password changed. Other devices were signed out.");
              })
            }
          >
            Change password
          </Button>
        </section>

        <section className="account-card">
          <Tape color="#d98c8c" className="auth-tape" />
          <h2>Recovery key</h2>
          {newKey ? (
            <RecoveryKeyCard recoveryKey={newKey} onDone={() => setNewKey(null)} doneLabel="Done" />
          ) : (
            <>
              <p className="muted">Lost your recovery key? Make a new one (the old one stops working).</p>
              <label className="field">
                <span>Password</span>
                <input type="password" value={keyPw} onChange={(e) => setKeyPw(e.target.value)} autoComplete="current-password" />
              </label>
              <Button disabled={!!busy || !keyPw} onClick={() => run("key", async () => { const r = await api.newRecoveryKey(await deriveKey(keyPw, user.email)); setKeyPw(""); setNewKey(r.recoveryKey); })}>
                Make a new recovery key
              </Button>
            </>
          )}
        </section>

        {guestCount > 0 && (
          <section className="account-card">
            <Tape color="#8aa3bf" className="auth-tape" />
            <h2>Boards on this device</h2>
            <p className="muted">
              {guestCount} board{guestCount === 1 ? " was" : "s were"} made here without an account.
            </p>
            <Button kind="primary" disabled={!!busy} onClick={() => run("import", async () => { const n = await importGuestBoards(); setGuestCount(0); toast(`Moved ${n} board${n === 1 ? "" : "s"} into your account`); })}>
              {busy === "import" ? "Moving…" : "Move them into my account"}
            </Button>
          </section>
        )}

        <section className="account-card danger-card">
          <Tape color="#c2583a" className="auth-tape" />
          <h2>Leave</h2>
          <Button onClick={() => signOut().then(() => navigate("/welcome"))}>Log out</Button>
          <hr />
          <p className="muted">Deleting your account removes every board, image and share link. There's no undo.</p>
          <label className="field">
            <span>Password to confirm</span>
            <input type="password" value={delPw} onChange={(e) => setDelPw(e.target.value)} autoComplete="current-password" />
          </label>
          <Button
            kind="danger"
            disabled={!!busy || !delPw}
            onClick={() => {
              if (!window.confirm("Delete your account and all boards forever?")) return;
              run("delete", async () => {
                await api.deleteAccount(await deriveKey(delPw, user.email));
                setUser(null);
                navigate("/welcome");
              });
            }}
          >
            Delete my account
          </Button>
        </section>
      </div>
    </div>
  );
}
